import React, { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { UPSERT_COMPANY_HOLIDAY } from '../../graphql/team-availability-mutations';
import { toast } from 'react-hot-toast';
import { toCalendarDateKey } from '../../utils/availabilityDateUtils';

function HolidayFormModal({ isOpen, onClose, holiday, onSaved }) {
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    description: ''
  });

  useEffect(() => {
    if (holiday) {
      const dateStr = toCalendarDateKey(holiday.date);
      setFormData({
        name: holiday.name || '',
        date: dateStr,
        description: holiday.description || ''
      });
    } else {
      setFormData({
        name: '',
        date: '',
        description: ''
      });
    }
  }, [holiday]);

  const [upsertHoliday, { loading }] = useMutation(UPSERT_COMPANY_HOLIDAY, {
    onCompleted: () => {
      toast.success(holiday ? 'Holiday updated successfully' : 'Holiday created successfully');
      onSaved?.();
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to save holiday');
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.date) {
      toast.error('Name and date are required');
      return;
    }

    upsertHoliday({
      variables: {
        input: {
          id: holiday?.id,
          name: formData.name,
          date: new Date(`${formData.date}T00:00:00.000Z`),
          description: formData.description || null
        }
      }
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {holiday ? 'Edit Holiday' : 'Add Holiday'}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 disabled:opacity-50"
              >
                {loading ? 'Saving...' : holiday ? 'Update Holiday' : 'Create Holiday'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default HolidayFormModal;

